'use client';

import { platform } from '@todesktop/client-core';
import { usePathname, useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { createContext, use, useEffect, useMemo, useRef } from 'react';

// Routes that are not allowed in the ToDesktop app
const RESTRICTED_DESKTOP_ROUTES = ['/', '/download'];

// Create a context to expose desktop status
type ToDesktopContextType = {
  isDesktopApp: boolean;
};

const ToDesktopContext = createContext<ToDesktopContextType | undefined>(undefined);

/**
 * Hook to access ToDesktop status
 */
export function useToDesktop() {
  const context = use(ToDesktopContext);
  if (context === undefined) {
    throw new Error('useToDesktop must be used within a ToDesktopAuthProvider');
  }
  return context;
}

/**
 * ToDesktopAuthProvider checks if the app is running in ToDesktop environment
 * and restricts access to specific routes when running as a desktop app.
 */
export function ToDesktopAuthProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const isNavigatingRef = useRef(false);
  const resetTimerRef = useRef<number | null>(null);

  // Get desktop status once at initialization
  const isDesktopApp = typeof window !== 'undefined' ? platform.todesktop.isDesktopApp() : false;

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ isDesktopApp }), [isDesktopApp]);

  useEffect(() => {
    // Only redirect if:
    // 1. Is a desktop app
    // 2. Current path is in restricted routes
    // 3. Not currently navigating
    if (isDesktopApp && RESTRICTED_DESKTOP_ROUTES.includes(pathname) && !isNavigatingRef.current) {
      console.log('Redirecting restricted route to inbox:', pathname);
      isNavigatingRef.current = true;
      router.replace('/inbox');
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
      resetTimerRef.current = window.setTimeout(() => {
        isNavigatingRef.current = false;
        resetTimerRef.current = null;
      }, 500); // Reset after navigation
      return () => {
        if (resetTimerRef.current !== null) {
          window.clearTimeout(resetTimerRef.current);
          resetTimerRef.current = null;
        }
        // Reset the flag so future navigations to restricted routes can trigger redirects
        isNavigatingRef.current = false;
      };
    }
    return undefined;
  }, [isDesktopApp, pathname, router]);

  return <ToDesktopContext value={contextValue}>{children}</ToDesktopContext>;
}
